import {ArrowRight, Check, Close, ExpandMore, MoreVert, North, Search, Sort, South, TableRowsOutlined, ViewAgendaOutlined,
        ViewColumnOutlined} from "@mui/icons-material";
import {Avatar, Button, Checkbox, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import {DataGridPro} from "@mui/x-data-grid-pro";
import {LicenseInfo} from "@mui/x-license";
import React, {Component} from "react";
import {withAppContext} from "../util/withAppContext";
import {Black, Blue3, Grey11, Grey4, Grey5} from "../theme/styles";
import {prepareBuffer} from "../util/Buffer";
import {formatLocal, formatLocalDate} from "../util/Formatters";
import css_cres_table from "./Common/css/PlatformTable.module.scss";
import css_button from "./Common/css/CCButton.module.scss";
import withBreakpoint from "../util/withBreakpoint";
import MemoryContext from "../util/MemoryContext";
import EnterpriseComponent from "../eoec/ec/EnterpriseComponent";
import {makeEnterpriseComponent} from "../util/utility";
import SimpleSearchFormApplet from "../eoec/applet/impl/SimpleSearchFormApplet";
import EventBus from "../util/EventBus";
import {fullTextTopic} from "../util/tableSearch";
import spinner from "../assets/Spinner-1s-200px.svg";
import {statusPill} from "../util/statusPill";

LicenseInfo.setLicenseKey(
    "7b694f3ee56dd278d118cb07d959ee9eTz0xMjcxMDksRT0xODA1NTAwNzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y"
);

export class PlatformTable extends Component {
    state = {
        fetchBackgroundOperation: false,
        buffer: [],
        previousBuffer: [],
        showRowsToDisplayButtons: false,
        showColumnSelection: false,
        columnSelection: {},
        // User-chosen column visibility: {field: false} hides a column. Only OPTIONAL columns
        // (metadata hideable !== false) are ever hidden; required + chrome columns stay visible.
        // Seeded from sessionStorage (per applet) in the constructor; toggled via the Columns menu.
        columnVisibilityModel: {},
        columnMenuTarget: null,
        showMenu: false,
        sortMenuTarget: null,
        searchMenuTarget: null,
        menuTarget: null,
        showSearch: false,
        sortSpecification: null,
        showSortSelection: false,
        activeSort: null,
        queryController: this,
    };

    constructor(props) {
        super(props);

        this.applet = props.applet;

        this.searchSpecification = null;

        this.sortSpecification = {Options: []};
        this.sortSelections = null;

        this.searchEnterpriseComponent = makeEnterpriseComponent(this.applet.getEnterpriseComponent().getName());
        this.searchEnterpriseComponent.companionEnterpriseObject = this.applet.getEnterpriseComponent().getEnterpriseObject();

        MemoryContext.components[`PlatformTable-${this.getName()}`] = {}
        this.memoryState = MemoryContext.components[`PlatformTable-${this.getName()}`];
        if (this.memoryState.pageSize === undefined) {
            // A TABLE THAT OPENS AS CARDS OPENS AT THE CARD'S PAGE SIZE. `toggleCardView` set this
            // and the constructor did not, so a table with `defaultOn` drew its first page at the
            // GRID's forty -- forty cards, which is the scroll the five exists to prevent. The
            // default is only ever read once, and this is the only place it is read.
            const opensAsCards = this.props.card
                && (this.memoryState.cardView ?? this.props.card.defaultOn === true);
            this.memoryState.pageSize = opensAsCards
                ? (this.props.card.pageSize ?? 10)
                : (this.props.pageSize?.default ?? 5);
        }
        if (this.memoryState.numberOfPages === undefined) this.memoryState.numberOfPages = 1;
        if (this.memoryState.pageToDisplay === undefined) this.memoryState.pageToDisplay = 1;

        this.state.columnVisibilityModel = this.loadColumnVisibility();
    }

    // ---- Column visibility (show/hide columns) -------------------------------------------------
    // Required columns = metadata hideable === false; chrome columns = no headerTitle. Neither can
    // be hidden. Everything else is optional and user-toggleable via the Columns menu; the choice
    // is persisted per applet in sessionStorage (as the list of hidden field ids).

    columnVisibilityStorageKey = () => `platformTable.cols.${this.getName()}`;

    // Default visibility from column metadata: an OPTIONAL column with `show === false` starts hidden
    // (still available in the Columns menu). Required (hideable === false) and chrome columns (no
    // headerTitle) always show. Used only when the user has made no explicit choice yet.
    defaultColumnVisibility = () => {
        const model = {};
        (this.props.columns || []).forEach((col) => {
            if (col.hideable === false || !col.headerTitle) return;
            if (col.show === false) model[col.id] = false;
        });
        return model;
    };

    loadColumnVisibility = () => {
        try {
            const raw = sessionStorage.getItem(this.columnVisibilityStorageKey());
            if (raw !== null) {
                // The user has customized this table before — their saved choice is authoritative.
                const hidden = JSON.parse(raw);
                const model = {};
                if (Array.isArray(hidden)) hidden.forEach((field) => { model[field] = false; });
                return this.sanitizeColumnVisibility(model);
            }
        } catch (e) { /* fall through to metadata defaults */ }
        return this.defaultColumnVisibility();
    };

    persistColumnVisibility = (model) => {
        try {
            const hidden = Object.keys(model).filter((field) => model[field] === false);
            sessionStorage.setItem(this.columnVisibilityStorageKey(), JSON.stringify(hidden));
        } catch (e) { /* sessionStorage unavailable — keep in-memory only */ }
    };

    sanitizeColumnVisibility = (model) => {
        const safe = {...model};
        (this.props.columns || []).forEach((col) => {
            if (col.hideable === false || !col.headerTitle) delete safe[col.id];
        });
        return safe;
    };

    onColumnVisibilityModelChange = (model) => {
        const safe = this.sanitizeColumnVisibility(model);
        this.persistColumnVisibility(safe);
        this.setState({columnVisibilityModel: safe});
    };

    setColumnVisibility = (field, visible) => {
        const model = {...this.state.columnVisibilityModel};
        if (visible) delete model[field]; else model[field] = false;
        this.onColumnVisibilityModelChange(model);
    };

    componentDidMount() {

        EventBus.components.addListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-cancel", this.onSearchCancelHandler);
        EventBus.components.addListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-reset", this.onSearchResetHandler);
        // ON THE LIST COMPONENT'S SERIAL, not the search component's, because the page that owns
        // the header box holds the LIST's enterprise component and has no way to reach the private
        // one this table makes for searching.
        EventBus.components.addListener(fullTextTopic(this.applet.getEnterpriseComponent()),
                                        this.onFullTextSearchHandler);

        this.applet.getEnterpriseComponent().addRecordChangedListener(this);
        this.applet.getEnterpriseComponent().addNewQueryRecordListener(this);
        this.applet.getEnterpriseComponent().addLoadStartedListener(this);
        this.applet.getEnterpriseComponent().addLoadCompletedListener(this);
        this.applet.getEnterpriseComponent().addValueChangedListener(this);
        this.applet.getEnterpriseComponent().addInvalidListener(this);
        this.applet.getEnterpriseComponent().addUpdateStartedListener(this);
        this.applet.getEnterpriseComponent().addUpdateCompletedListener(this);
        this.applet.getEnterpriseComponent().addDeleteCompletedListener(this);
        this.applet.getEnterpriseComponent().addInsertStartedListener(this);
        this.applet.getEnterpriseComponent().addInsertCompletedListener(this);
        this.applet.getEnterpriseComponent().addInsertRollbackCompletedListener(this);
        this.applet.getEnterpriseComponent().addQueryRollbackCompletedListener(this);
        this.applet.getEnterpriseComponent().addSingleRecordRefreshStartedListener(this);
        this.applet.getEnterpriseComponent().addSingleRecordRefreshCompletedListener(this);

        this.searchEnterpriseComponent.addLoadStartedListener(this);
        this.searchEnterpriseComponent.addLoadCompletedListener(this);
        this.searchEnterpriseComponent.addInvalidListener(this);

        this.makeSortSpecification(null);

        if (this.state.action === "New") {
            this.applet.getEnterpriseComponent().newRecord();
        } else {
            this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
            if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
            this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility()?.Mode);
            this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility()?.Value);
            this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        }
    }

    /** Keep the chosen sort with the page and page size, which already survive navigation.
     *
     * memoryState is MemoryContext.components[`PlatformTable-<name>`] -- per table, and outliving
     * the component. Without this, opening a record and coming back rebuilt the table from props
     * and silently reverted to the applet's default sort, throwing away a choice the user had made
     * two clicks earlier. Reported by Ed.
     */
    rememberSort = (sort) => {
        if (!sort || sort.Identifier === undefined) return;
        this.memoryState.sortIdentifier = sort.Identifier;
        this.memoryState.sortDirections = (sort.Options ?? []).reduce((out, option) => {
            out[option.SortAttribute] = option.Direction;
            return out;
        }, {});
    };

    /** The remembered selection out of a freshly built list, or undefined if there is none.
     *
     * Matched by Identifier rather than kept by reference: the selections are rebuilt from props on
     * every mount, so the remembered object is not one of them. A remembered sort that no longer
     * exists -- an applet whose sort list changed between releases -- simply does not match, and
     * the default applies.
     */
    rememberedSort = (sortSelections) => {
        const remembered = sortSelections.find(s => s.Identifier === this.memoryState.sortIdentifier);
        if (!remembered) return undefined;
        // The direction is remembered too, so a flipped column stays flipped.
        for (const option of remembered.Options ?? []) {
            const direction = this.memoryState.sortDirections?.[option.SortAttribute];
            if (direction) option.Direction = direction;
        }
        return remembered;
    };

    makeSortSpecification = (sortModel) => {
        let sortSelections = [];
        let sortSpecification = {};
        let sortSelection = {};
        sortSelection["Identifier"] = "Row Identity";
        sortSelection["Options"] = [{
            "DisplayName": "Creation Datetime",
            "Direction": "DESC",
            "SortAttribute": "RowIdentity",
        }];
        // NOT OFFERED IN THE MENU. This is the framework's fallback ordering, kept so a grid that
        // declares no sorts of its own still has one -- the branch below already makes it the
        // invisible default in exactly that case. Every other grid declares its own, and showing
        // this alongside them put a second date entry in the menu: "Creation Datetime" sitting next
        // to the grid's own "Created", reading as a duplicate of it.
        //
        // WORSE THAN A DUPLICATE, because it does not sort. RowIdentity is not an attribute most
        // IDOCs expose -- v1.ObjectRevision-001, which backs every Foundation list, has no such tag
        // -- so choosing it asked the server to order by a column that is not in the payload.
        // Reported by Ed against the Purchase Order table.
        sortSelection["Visible"] = false;
        sortSelection["Default"] = false;
        sortSelections.push(sortSelection);

        if (this.props.sort !== undefined && this.props.sort !== null) {
            for (let i = 0; i < this.props.sort.length; i++) {
                sortSelection = {};
                sortSelection["Identifier"] = this.props.sort[i].Identifier;
                sortSelection["Visible"] = this.props.sort[i].Visible;
                sortSelection["Options"] = this.props.sort[i].Options;
                sortSelection["Default"] = this.props.sort[i].Default === true;
                sortSelections.push(sortSelection);
            }
        }

        if (sortModel !== null) {
            if (sortModel.length === 0) {
                sortSpecification = this.sortSpecification;
                if (sortSpecification["Options"][0]["Direction"] === "desc") {
                    sortSpecification["Options"][0]["Direction"] = "asc";
                } else {
                    sortSpecification["Options"][0]["Direction"] = "desc";
                }
                sortSelections = this.sortSelections;
            } else {
                for (let i = 0; i < sortModel.length; i++) {
                    for (let j = 0; j < sortSelections.length; j++) {
                        for (let t = 0; t < sortSelections[j]["Options"].length; t++) {
                            if (sortSelections[j].Options[t]["SortAttribute"] === sortModel[i]["field"]) {
                                sortSelections[j].Options[t]["Direction"] = sortModel[i]["sort"];
                            }
                        }
                    }
                }
            }
        }

        if (sortSelections.length === 1) {
            sortSelections[0].Default = true;
            sortSelections[0].Visible = false;
        }

        // THE REMEMBERED SORT WINS OVER THE APPLET'S DEFAULT. Default is what a table opens with the
        // first time; it is not what it should revert to after the user has chosen something else
        // and merely visited a record.
        const remembered = this.rememberedSort(sortSelections);
        if (remembered) {
            sortSpecification["Identifier"] = remembered.Identifier;
            sortSpecification["Visible"] = remembered.Visible;
            sortSpecification["Options"] = remembered.Options;
        } else {
            for (let i = 0; i < sortSelections.length; i++) {
                if (sortSelections[i].Default === true) {
                    sortSpecification["Identifier"] = sortSelections[i].Identifier;
                    sortSpecification["Visible"] = sortSelections[i].Visible;
                    sortSpecification["Options"] = sortSelections[i].Options;
                }
            }
        }

        this.setSortSelections(sortSelections);
        this.setSortSpecification(sortSpecification);
        this.setState({sortSpecification: sortSpecification, activeSort: sortSpecification, mode: this.applet.mode});
    };

    componentWillUnmount() {
        this.applet.getEnterpriseComponent().removeAllListeners(this);
        this.searchEnterpriseComponent.removeAllListeners(this);

        EventBus.components.removeListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-cancel", this.onSearchCancelHandler);
        EventBus.components.removeListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-reset", this.onSearchResetHandler);
        EventBus.components.removeListener(fullTextTopic(this.applet.getEnterpriseComponent()),
                                           this.onFullTextSearchHandler);
    }

    doneColumnSelectionHandler() {
        this.setState({showColumnSelection: false});
    };

    doneSortSelectionHandler() {
        this.setState({showSortSelection: false});
    };

    enterpriseComponentInsertStarted(ec) {

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: true});
    };

    enterpriseComponentInsertCompleted(ec){
        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});

        ec.invalidate();
    };

    enterpriseComponentDeleteCompleted(ec) {
        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() - 1 ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});

        ec.invalidate();
    };

    enterpriseComponentInvalidated(ec) {
        this.memoryState.pageToDisplay = 1;

        this.setState({buffer: prepareBuffer(ec)});

        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);

        this.applet.getEnterpriseComponent().executeQuery(1, this.memoryState.pageSize);
    };

    enterpriseComponentInsertRollbackCompleted(ec) {
        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentLoadStarted(ec) {
        this.setState({fetchBackgroundOperation: true, previousBuffer: this.state.buffer});
    };

    enterpriseComponentLoadCompleted(ec) {
        if (ec.serialNumber === this.applet.getEnterpriseComponent().serialNumber) {
            this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
            this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

            this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false, queryController: ec});
        }
        if (ec.serialNumber === this.searchEnterpriseComponent.serialNumber) {
            this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.searchEnterpriseComponent.getTotalRowcount() ?? 0) / this.memoryState.pageSize));
            this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

            this.applet.getEnterpriseComponent().resultsBuffer = this.searchEnterpriseComponent.resultsBuffer;
            this.applet.getEnterpriseComponent().totalRowcount = this.searchEnterpriseComponent.totalRowcount;
            this.applet.getEnterpriseComponent().setMode(EnterpriseComponent.MODE_RESULTS);
            this.applet.getEnterpriseComponent().setIsValid(true);

            this.applet.getEnterpriseComponent().invalidateChildren();

            let queryBuffer = this.searchEnterpriseComponent.queryBuffer;
            this.searchEnterpriseComponent.newQuery();
            this.searchEnterpriseComponent.queryBuffer = queryBuffer;
            this.applet.getEnterpriseComponent().setPagePointer(this.memoryState.pageToDisplay);
            this.setState({buffer: prepareBuffer(this.applet.getEnterpriseComponent()), fetchBackgroundOperation: false, queryController: ec});
        }
    };

    enterpriseComponentValueChanged(ec) {
        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentNewQueryRecordStarted(ec) {
       this.setState({buffer: prepareBuffer(ec)});
    };

    enterpriseComponentQueryRollbackCompleted(ec) {
        this.setState({buffer: prepareBuffer(ec)});

        this.applet.getEnterpriseComponent().executeQuery(1, this.memoryState.pageSize);
        // this.setSearchSpecification(null);
        // this.applet.getEnterpriseComponent().invalidate();
        // this.setState({showSearch: false, searchMenuTarget: null});
    };

    enterpriseComponentRecordChanged(ec){
        this.setState({buffer: prepareBuffer(ec)});
    };

    enterpriseComponentUpdateStarted(ec) {
    };

    enterpriseComponentUpdateCompleted(ec){

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentSingleRecordRefreshStarted(ec) {
    };

    enterpriseComponentSingleRecordRefreshCompleted(ec) {

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        let buffer = prepareBuffer(ec);
        this.setState({buffer: buffer});
    };

    getCellRenderer(name) {
        if (name === "Row Selection Indicator Cell") {
            return this.renderRowSelectionIndicatorCell;
        } else if (name === "Standard Single Cell") {
            return this.renderStandardSingleCell;
        } else if (name === "Account User Name Cell") {
            return this.renderAccountUserNameCell;
        } else if (name === "Local Date Cell") {
            return this.renderLocalDateCell;
        } else if (name === "Local Datetime Cell") {
            return this.renderLocalDatetimeCell;
        } else if (name === "Timing Cell") {
            return this.renderTimingCell;
        } else if (name === "Edit Cell") {
            return this.renderEditCell;
        }

        console.log("Unknown cell renderer \"" + name + "\"");
        return null;
    }

    _getColumns = (breakpoint, loading) => {
        let columns = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let columnMetadata = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id);
            let headerRenderer = columnMetadata.headerRenderer;

            let sortable = false;
            if (this.getSortSpecification() !== null && this.getSortSpecification().Options !== undefined) {
                for (let i = 0; i < this.getSortSpecification().Options.length; i++) {
                    if (columnMetadata.id === this.getSortSpecification().Options[i].SortAttribute) {
                        sortable = true;
                    }
                }
            }
            let headerAlign = columnMetadata.headerAlign;
            if (headerAlign === undefined) {
                headerAlign = "Left"
            }
            let column = {
                field: columnMetadata.id,
                headerName: columnMetadata.headerTitle,
                editable: false,
                sortable: false,
                filterable: false,
                headerAlign: headerAlign,
                resizable: columnMetadata.resizable,
                pinnable: false,
                disableColumnMenu: true,
                hideable: columnMetadata.hideable
            };
            if (columnMetadata.flex !== undefined) {
                column["flex"] = columnMetadata.flex;
            } else if (columnMetadata.width !== undefined) {
                column["width"] = columnMetadata.width + (sortable ? 45 : 0);
            }
            // A FLOOR UNDER A FLEXED COLUMN. flex alone lets a column shrink to nothing on a
            // narrow window; minWidth is what stops a flexed Name collapsing behind its own
            // ellipsis. Independent of the branch above because it qualifies flex, not width.
            if (columnMetadata.minWidth !== undefined) {
                column["minWidth"] = columnMetadata.minWidth;
            }
            if (loading) {
                if (this.state.previousBuffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
            } else {
                if (this.state.buffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
            }
            if (headerRenderer === null) {
                column["renderHeader"] = null;
            } else {
                column["renderHeader"] = (params) => this.getHeaderRenderer(columnMetadata.headerRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
            }
            columns.push(column);
        }
        return columns;
    };

    getColumns = (breakpoint, loading) => {
        // ONE COLUMN AND THE PLACEMENT IS IRRELEVANT in card view: the card reads the buffer, so
        // which columns a breakpoint would have placed changes nothing about what it can show.
        if (this.cardViewActive()) return this.getCardColumns();

        if (breakpoint === undefined) {
            breakpoint = "xl";
        }

        let columns = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("sm", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("md", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("lg", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("xl", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "xl") {
            columns = this._getColumns(breakpoint, loading);
        }

        return columns;
    };

    getColumnCount = (breakpoint) => {
        let columnCount = 0;

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("sm");
            } else {
                columnCount = this.props.placement["xs"].length;
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("md");
            } else {
                columnCount = this.props.placement["sm"].length;
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("lg");
            } else {
                columnCount = this.props.placement["md"].length;
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("xl");
            } else {
                columnCount = this.props.placement["lg"].length;
            }
        } else if (breakpoint === "xl") {
            columnCount = this.props.placement["xl"].length;
        }

        return columnCount;
    };

    // A PROTOTYPE METHOD, LIKE getCellRenderer BESIDE IT, and that symmetry is the point rather
    // than tidiness. This was an arrow CLASS FIELD, which has two consequences a subclass cannot
    // work around: `super.getHeaderRenderer` does not exist, because a field lives on the INSTANCE
    // and not the prototype; and a subclass field of the same name REPLACES this one outright
    // instead of wrapping it, so overriding it to add one renderer silently dropped "Standard
    // Single Header" and the empty header for every column in the grid.
    //
    // ObjectRevisionTable hit exactly that adding a select-all header to its tick column: every
    // header in the table threw "getHeaderRenderer is not a function" and the page would not
    // render. getCellRenderer has always been a method, which is why the cell override beside it
    // worked and this one could not.
    //
    // Both call sites invoke it as `this.getHeaderRenderer(...)`, so nothing depends on the bound
    // arrow; it is never detached from the instance.
    // WHICH CLASSES A ROW WEARS. A prototype METHOD and not an inline arrow in the JSX, so a
    // subclass can add its own and still get this one through `super` -- the mistake
    // getHeaderRenderer made by being a class field, which no subclass could reach.
    //
    // cc-row-selected is the EC's CURRENT RECORD, which is not the same thing as a row the reader
    // picked: every list sets it to row 0 the moment a query returns (EnterpriseComponent.ts:638).
    rowClassName(params) {
        // NOT IN CARD VIEW. `cc-row-selected` marks the Enterprise Component's CURRENT record,
        // which every list sets to row 0 the moment a query returns -- nobody chose it, as
        // index.css says in as many words. On a 48px row the blue top and bottom border reads as
        // a faint cue; on a card four times that height it draws a frame around the first card
        // and says "this one is special", which is a statement about the data that is not true.
        // Reported by Ed within a minute of seeing it: "the first row of the card view has a blue
        // line above and below the row".
        if (this.cardViewActive()) return "";
        return params.row.RowSelected ? "cc-row-selected" : "";
    }

    getHeaderRenderer(name) {
        if (name === "Standard Single Header") {
            return this.renderStandardSingleHeader;
        } else if (name === "") {
            return this.renderEmptyHeader
        }

        return null;
    }

    _getHeaderCells = (breakpoint) => {
        let cells = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let headerRenderer = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id).headerRenderer;

            if (headerRenderer === null) console.log("Header renderer not found");
            else {
                cells.push(this.getHeaderRenderer(headerRenderer)(this.props.placement[breakpoint][columnIndex].id));
            }
        }

        return cells;
    };

    getHeaderCells = (breakpoint) => {
        let cells = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("sm");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("md");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("lg");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("xl");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "xl") {
            cells = this._getHeaderCells(breakpoint);
        }

        return cells;
    };

    getName = () => {
        return this.applet.getName();
    };

    _getPinnedColumns = (breakpoint) => {
        let leftPinnableColumns = [];
        let rightPinnableColumns = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let columnMetadata = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id);
            if (columnMetadata.pinned === undefined || columnMetadata.pinned === false) {
                continue;
            }
            let headerRenderer = columnMetadata.headerRenderer;

            if (headerRenderer === null) console.log("Header renderer not found");
            else {
                let sortable = false;
                for (let i = 0; i < this.getSortSpecification().Options.length; i++) {
                    if (columnMetadata.id === this.getSortSpecification().Options[i].SortAttribute) {
                        sortable = true;
                    }
                }
                let column = {
                    field: columnMetadata.id,
                    headerName: columnMetadata.headerTitle,
                    editable: false,
                    sortable: sortable,
                    filterable: columnMetadata.filterable,
                    headerAlign: columnMetadata.align,
                    resizable: columnMetadata.resizable,
                    hideable: columnMetadata.hideable,
                };
                if (columnMetadata.flex !== undefined) {
                    column["flex"] = columnMetadata.flex;
                } else if (columnMetadata.width !== undefined) {
                    column["width"] = columnMetadata.width;
                }
                if (this.state.buffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
                if (columnMetadata.pinned.toLowerCase() === "right") {
                    rightPinnableColumns.push(column);
                } else if (columnMetadata.pinned.toLowerCase() === "left") {
                    leftPinnableColumns.push(column);
                }
            }
        }
        let out = {};
        if (leftPinnableColumns.length > 0) {
            out["left"] = [];
        }
        for (let i = 0; i < leftPinnableColumns.length; i++) {
            out["left"].push(leftPinnableColumns[i].field);
        }
        if (rightPinnableColumns.length > 0) {
            out["right"] = [];
        }
        for (let i = 0; i < rightPinnableColumns.length; i++) {
            out["right"].push(rightPinnableColumns[i].field);
        }
        return out;

    };

    getPinnedColumns = (breakpoint) => {
        if (breakpoint === undefined) {
            breakpoint = "xl";
        }

        let columns = {};

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("sm");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("md");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("lg");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("xl");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "xl") {
            columns = this._getPinnedColumns(breakpoint);
        }

        return columns;
    };

    _getRowCells = (rowIndex, breakpoint) => {
        let cells = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let cellRenderer = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id).cellRenderer;
            cells.push(this.getCellRenderer(cellRenderer)(this, rowIndex, this.props.placement[breakpoint][columnIndex].id, breakpoint));
        }

        return cells;
    };

    getRowCells = (rowIndex, breakpoint) => {
        let cells = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "sm");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "md");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "lg");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "xl");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "xl") {
            cells = this._getRowCells(rowIndex, breakpoint);
        }

        return cells;
    };

    /**
     * Read ANY attribute of the record behind a row, whether or not it is a column.
     *
     * WHY A CELL CANNOT JUST USE params.row. getRows below builds each row from the PLACED COLUMNS
     * ONLY -- one `row[id] = buffer[i].get(id)` per placed column and nothing else -- so an
     * attribute the EC holds, the IDOC carries and the server returned is still `undefined` on the
     * row unless somebody put it on screen. A renderer that reads a usid to build a link, or a
     * second revision number to detect drift, is reading exactly the kind of attribute nobody
     * places, and it fails SILENTLY: the link renders as plain text, the drift icon never appears,
     * and the grid looks finished. That is a real defect twice over in the change order screens.
     *
     * The buffer is the record, so this goes to the buffer. params.row is consulted first only so a
     * placed column still reads correctly while a background fetch is swapping the buffer out.
     */
    readAttribute = (params, field) => {
        const row = params?.row;
        if (row && row[field] !== undefined && row[field] !== null) {
            return row[field];
        }
        const record = this.state.buffer?.[row?.id];
        return (record?.get ? record.get(field) : undefined) ?? null;
    };

    /**
     * CARD VIEW: one column, and the cell is the record.
     *
     * A GRID ANSWERS "WHICH ONE", A CARD ANSWERS "WHAT IS THIS". Both are useful and neither
     * replaces the other, so this is a view of the same table rather than a different table: the
     * same EC, the same query, the same paging and sorting, the same search. Only the painting
     * changes, which is why it lives here rather than in a second component somebody would have to
     * keep in step.
     *
     * IT READS THE BUFFER, NOT THE ROW, and that is the whole reason this is possible. getRows
     * builds each row from the PLACED columns only, so in card view -- where exactly one synthetic
     * column is placed -- a row carries nothing but its id. `readAttribute` falls through to the
     * record in the buffer, which holds every attribute the EC fetched whether or not anybody put
     * it on screen. A card can therefore surface the fields a detail page shows without the table
     * having to place twelve columns it never draws.
     *
     * TEN ROWS A PAGE BY DEFAULT, because a card is three or four times the height of a row and
     * forty of them is a scroll nobody reads. Ten rather than five after seeing it: five left most
     * of the window empty on a list of a handful of folders, which reads as the page having run
     * out rather than as a page. The grid's own page size is left alone and restored when the view
     * is switched back -- two views of one table should not fight over one setting.
     */
    cardViewActive = () => {
        if (!this.props.card) return false;
        return this.memoryState.cardView ?? this.props.card.defaultOn === true;
    };

    toggleCardView = () => {
        const next = !this.cardViewActive();
        // THE PAGE SIZE BELONGS TO THE VIEW, so each remembers its own and neither clobbers the
        // other. Switching to cards drops to five; switching back restores whatever the grid had.
        if (next) {
            this.memoryState.gridPageSize = this.memoryState.pageSize;
            this.memoryState.pageSize = this.props.card.pageSize ?? 10;
        } else {
            this.memoryState.pageSize = this.memoryState.gridPageSize
                ?? this.props.pageSize?.default ?? 40;
        }
        this.memoryState.cardView = next;
        this.memoryState.pageToDisplay = 1;
        // INVALIDATE, THEN QUERY. `executeQuery` alone is satisfied by the page already in the
        // buffer, so switching from five cards to a forty-row grid redrew the same five records
        // under a footer reading "Results per page: 40 / 1 - 6 of 6" -- a count, a page size and a
        // row count that disagreed three ways. The component has to be told its page is stale.
        this.applet.getEnterpriseComponent().invalidate();
        this.applet.getEnterpriseComponent().executeQuery(1, this.memoryState.pageSize);
        this.forceUpdate();
    };

    /**
     * One field of a card: the quiet grey label over the value, as the detail pane draws it.
     *
     * A RENDERED VALUE IS PLACED AS-IS, which this did not do and had to learn twice. `String(x)`
     * on a React element is "[object Object]" -- so a field returning a row of icons drew that
     * text instead, with no error anywhere. ObjectDetail's own `Field` helper carries the same
     * guard and the same note ("a rendered value -- the status pill -- is placed as-is"); it did
     * not get carried across when this was written.
     *
     * WRAPPING AN ELEMENT IN THE Typography BELOW WOULD ALSO BE WRONG even when it renders: it
     * would put a chip or an icon inside a paragraph and inherit the paragraph's font size onto
     * it. The row keeps its height either way, so a field of icons lines up with a field of text.
     */
    renderCardField = (label, value) => {
        const rendered = React.isValidElement(value);
        const empty = value === null || value === undefined || value === "";
        return (
            <div key={label} style={{display: "flex", flexDirection: "column", gap: 1,
                                     minWidth: 190}}>
                <Typography style={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>{label}</Typography>
                {rendered ? (
                    <div style={{display: "flex", alignItems: "center", minHeight: 21}}>{value}</div>
                ) : (
                    <Typography style={{fontSize: 13, color: Black, lineHeight: 1.4}}>
                        {empty ? "\u2014" : String(value)}
                    </Typography>
                )}
            </div>
        );
    };

    renderCard = (params) => {
        const card = this.props.card;
        const read = (field) => (typeof field === "function"
            ? field((one) => this.readAttribute(params, one))
            : this.readAttribute(params, field));

        if (card.render) return card.render((one) => this.readAttribute(params, one));

        // WHICH VALUE DECIDES. `whenSet: true` tests the field's own value, which is right for a
        // plain attribute -- and WRONG for a field rendered by a function, because a formatter
        // returns "\u2014" for an absent date rather than null. So `whenSet` may instead name the
        // attribute whose presence decides, and "Closed" names ClosedDatetime: without that every
        // open project carried a "Closed \u2014" field, which is a label telling the reader
        // nothing on the rows where the Status chip has already told them.
        const isSet = (value) => React.isValidElement(value)
            || ![null, undefined, "", "\u2014"].includes(value);
        // A NAMED RENDERER THAT DOES NOT EXIST MUST NOT TAKE THE PAGE DOWN WITH IT.
        // `getCellRenderer` logs the unknown name and returns NULL, and both of these call what it
        // hands back -- so a typo in `corner` or `chipRenderer` was a TypeError in the middle of a
        // list rather than a missing badge. That was survivable while the card was something a
        // reader opted into; it is the landing page of every object screen now. The chip falls
        // back to the plain pill, the corner to nothing, and the console still carries the name.
        const chipCell = card.chipRenderer ? this.getCellRenderer(card.chipRenderer) : null;
        const cornerCell = card.corner ? this.getCellRenderer(card.corner) : null;

        const fields = (card.fields ?? []).filter((one) => {
            if (one.whenSet === undefined || one.whenSet === false) return true;
            return isSet(typeof one.whenSet === "string"
                ? this.readAttribute(params, one.whenSet)
                : read(one.field));
        });

        return (
            <div style={{display: "flex", flexDirection: "column", gap: 10, width: "100%",
                         padding: "14px 4px"}}>
                <div style={{display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap"}}>
                    {/* THE TITLE MAY BE A CELL RENDERER TOO, the way the pill and the corner are:
                        the object lists draw their identifier as the two-door control (drawer,
                        new window) and a card must draw the same thing the grid's column does. */}
                    {card.titleRenderer ? (
                        <span style={{display: "inline-flex", alignItems: "center"}}>
                            {this.getCellRenderer(card.titleRenderer)(this, params, card.titleRenderer,
                                                                       this.props.breakpoint?.getBreakPointName())}
                        </span>
                    ) : (
                        <Typography style={{fontSize: 15, color: Black, fontWeight: 500}}>
                            {read(card.title) ?? "\u2014"}
                        </Typography>
                    )}
                    {card.subtitle ? (
                        <Typography style={{fontSize: 13, color: Grey5}}>
                            {read(card.subtitle)}
                        </Typography>
                    ) : null}
                    {/* THE PILL BELONGS WITH THE TITLE. It is a fact ABOUT the thing just
                        named -- RELEASED, OPEN -- so it reads as part of the name rather than as
                        a separate indicator. Putting the corner before it pushed the pill past it
                        to the far right, where it read as a second corner badge. Ed: "the status
                        pill should be just to the right of the title as you had it."

                        THE SHARED PILL, not a chip of this component's own. A plain outlined Chip
                        renders every status the same grey, so RELEASED read as neutral on a card
                        and green two feet away in the grid -- the same fact, two colours,
                        depending which view you were in. `statusPill` is what the grid's own
                        "Status Pill Cell" uses, and it also turns IN_REVIEW into "IN REVIEW"
                        rather than printing a piece of the database.

                        AND `chipRenderer` NAMES THE GRID'S OWN CELL INSTEAD, the way `corner`
                        below already does, for the lists whose pill carries more than the status.
                        An object revision's Status cell draws "RELEASED · in force 15 Jan" when a
                        revision is released but not yet governing -- two rows reading RELEASED and
                        only one of them in force, which is the duplicate Ed reported on TC-00002.
                        `statusPill(read("Status"))` CANNOT carry that: the marker is decided by
                        IsCurrent and EffectiveDatetime, not by the status. Making the object lists
                        open as cards would have dropped it silently -- the card would have drawn a
                        plain green RELEASED and been wrong in exactly the way the grid was fixed
                        for. Named rather than reimplemented, so the two cannot drift apart. */}
                    {chipCell ? (
                        <span style={{display: "inline-flex", alignItems: "center"}}>
                            {chipCell(this, params, card.chipRenderer,
                                      this.props.breakpoint?.getBreakPointName())}
                        </span>
                    ) : card.chip ? statusPill(read(card.chip)) : null}
                    {/* THE CORNER, for whatever a reader scanning a list of these would want to
                        see without opening one, and it is what floats right. It names an EXISTING
                        cell renderer rather than taking a node, so the card shows the same thing
                        the grid's own column shows -- Documents puts "Record Artefacts Cell" here,
                        which is the "Has" column, so the files/Docs/SBOM chips cannot say one
                        thing in a row and another on a card. */}
                    {cornerCell ? (
                        <span style={{marginLeft: "auto", display: "inline-flex",
                                      alignItems: "center"}}>
                            {cornerCell(this, params, card.corner,
                                        this.props.breakpoint?.getBreakPointName())}
                        </span>
                    ) : null}
                </div>
                {fields.length ? (
                    <div style={{display: "flex", flexWrap: "wrap", gap: "14px 34px"}}>
                        {fields.map((one) => this.renderCardField(one.label, read(one.field)))}
                    </div>
                ) : null}
            </div>
        );
    };

    getCardColumns = () => ([{
        field: "__card",
        headerName: this.props.card.headerName ?? "",
        editable: false,
        sortable: false,
        filterable: false,
        hideable: false,
        resizable: false,
        pinnable: false,
        disableColumnMenu: true,
        flex: 1,
        renderCell: (params) => this.renderCard(params),
    }]);

    getRows = (breakpoint, columns) => {
        let rows = [];

        for (let i = 0; i < this.state.buffer.length; i++) {
            let row = {
                id: i,
            };
            row["RowSelected"] = i === this.getSelectedRecordNumber();
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                let columnMetadata = this.props.columns.find((element) => element.id === columns[columnIndex].field);

                // A COLUMN WITH NO METADATA IS SKIPPED, not dereferenced. getLoadingRows below has
                // always guarded this and getRows never did, so any synthetic column -- the card
                // column added here, or anything a later table composes -- threw on the first
                // render rather than drawing nothing. The two loops are otherwise the same loop.
                if (columnMetadata === undefined) continue;

                row[columnMetadata.id] = this.state.buffer[i].get(columnMetadata.id);
            }
            rows.push(row);
        }
        return rows;
    };

    getLoadingRows = (breakpoint, columns) => {
        let rows = [];

        for (let i = 0; i < this.state.previousBuffer.length; i++) {
            let row = {
                id: i,
            };
            row["RowSelected"] = i === this.getSelectedRecordNumber();
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                let columnMetadata = this.props.columns.find((element) => element.id === columns[columnIndex].field);

                if (columnMetadata !== undefined) {
                    row[columnMetadata.id] = this.state.previousBuffer[i].get(columnMetadata.id);
                }
            }
            rows.push(row);
        }
        return rows;
    };

    getSearchSpecification = () => {
        return this.searchSpecification;
    };

    getSelectedRecordNumber = () => {
        return this.applet.getEnterpriseComponent().getSelectedRecordNumber();
    };

    getSortSelections = () => {
        if (this.sortSelections === null) return [];

        return this.sortSelections;
    };

    getSortSpecification = () => {
        return this.sortSpecification;
    };

    handleSortMenuClick = (sort) => {
        this.setState({sortMenuTarget: null, activeSort: sort});

        this.setSortSpecification(sort);
        this.rememberSort(sort);

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    };

    handleSortDirectionChange = (field) => {
        let newSort = structuredClone(this.state.activeSort);

        for (let i = 0; i < newSort.Options.length; i++) {
            let option = newSort.Options[i];
            if (option.SortAttribute === field) {
                option.Direction = option.Direction === "ASC" ? "DESC" : "ASC";
            }
        }
        this.setState({activeSort: newSort});

        this.setSortSpecification(newSort);
        this.rememberSort(newSort);

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    }

    handleMenuClick(identifier) {
        if (identifier === "Refresh") {
            this.applet.getEnterpriseComponent().invalidate();
        }
    };

    handlePageChange = (event, value) => {
        this.memoryState.pageToDisplay = value;
        if (this.state.queryController.serialNumber === this.applet.getEnterpriseComponent().serialNumber) {
            this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        } else if (this.state.queryController.serialNumber === this.searchEnterpriseComponent.serialNumber) {
            this.searchEnterpriseComponent.executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        }
    };

    onPageSizeHandler = (event) => {
        event.preventDefault();

        this.memoryState.pageSize = event.target.value;

        this.query();
    };

    onRowClick(params) {
        this.applet.getEnterpriseComponent().selectRecord(params.row.id);
    };

    onRowDoubleClick(params) {
        this.applet.getEnterpriseComponent().selectRecord(params.row.id);
    };

    /**
     * SEARCH THE INDEXED DOCUMENT, driven from somewhere other than the search form.
     *
     * THIS IS THE FORM'S OWN PATH, NOT A SECOND ONE. The "Search text" control in the simple
     * search form did exactly this: write the text and the operator onto the searching component's
     * query record and execute it. So this writes the same two attributes and executes the same
     * component, and the results arrive through the same `enterpriseComponentLoadCompleted` branch
     * that copies them onto the list. Nothing here knows what the predicate is -- the "Contains
     * Keywords" operator is what the IDOC turns into Operator "SEARCH", and the enterprise
     * component turns that into a tsquery against the indexed document.
     *
     * "Contains Keywords" IS THE WHOLE TRICK and it is a string, not a type. PlatformDetail sets
     * it from `type: "Search"` on the control; any other type sets "Contains", which is an equality
     * test against a JSONB column and matches nothing at all -- silently.
     *
     * AN EMPTY BOX CLEARS RATHER THAN SEARCHES FOR NOTHING. Blanking the field and pressing enter
     * has to give the reader their list back, so the operator is cleared with the text: a
     * "Contains Keywords" with no value is a predicate nobody can satisfy.
     *
     * PAGE 1, because the answer to a new question has nothing to do with how far through the old
     * list the reader had scrolled.
     */
    onFullTextSearchHandler = (text) => {
        const asked = (text ?? "").trim();
        const search = this.searchEnterpriseComponent;

        // The form calls newQuery when it mounts. If the reader never opened it, nobody has, and
        // there is no query record to write onto.
        if (search.queryBuffer.size === 0) search.newQuery();

        search.setAttributeValue("SearchableContent", asked);
        search.setAttributeValue("SearchableContent_query", asked ? "Contains Keywords" : "");

        this.memoryState.pageToDisplay = 1;
        search.setSortSpecification(this.getSortSpecification()?.Options);
        search.setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility()?.Mode);
        search.setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility()?.Value);
        search.executeQuery(1, this.memoryState.pageSize);
    };

    onSearchCancelHandler = () =>  {
        this.setState({showSearch: false});
    };

    onSearchResetHandler = () =>  {
        this.searchEnterpriseComponent.newQuery();

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility()?.Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility()?.Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    };

    openRow = (event, rowIndex) => {
        event.preventDefault();
        event.stopPropagation();

        let buffer = this.state.buffer;
        let status = buffer[rowIndex].openRow;
        if (status === undefined) status = false;

        buffer[rowIndex].openRow = !status;

        this.setState({openRow: !this.state.openRow});
    };

    query = (searchSpecification) => {
        this.setSearchSpecification(searchSpecification);

        this.applet.getEnterpriseComponent().invalidate();
    };

    renderLoadingTable = () => {
        let columns = this.getColumns(this.props.breakpoint?.getBreakPointName(), true);
        let pinnedColumns = this.getPinnedColumns(this.props.breakpoint?.getBreakPointName());
        let loadingRows = this.getLoadingRows(this.props.breakpoint?.getBreakPointName(), columns);

        let rowHeight = this.cardViewActive() ? "auto" : this.props.rowHeight;
        // THE SAME HEADER RULE AS THE LOADED GRID. Without it a fetch flashes a header bar the
        // view it is loading does not have, which reads as the list jumping.
        const headerless = this.cardViewActive() ? {columnHeaderHeight: 0} : {};
        if (rowHeight === "auto") {
            return(
                <DataGridPro key={this.cardViewActive() ? "cards" : "grid"}
                             style={{border: "1px solid #D8DDE4"}}
                             columns={columns}
                             pinnedColumns={pinnedColumns}
                             columnVisibilityModel={this.state.columnVisibilityModel}
                             rows={loadingRows}
                             fullWidth={true}
                             density="compact"
                             filterMode="client"
                             hideFooter={true}
                             hideFooterRowCount={true}
                             hideFooterPagination={true}
                             hideFooterSelectedRowCount={true}
                             loading={false}
                             {...headerless}
                             getRowHeight={() => 'auto'}
                    sx={{
                        '.MuiDataGrid-columnHeaderTitleContainer': {
                            display: "inline",
                        }
                    }}/>
            )
        } else {
            return(
                <DataGridPro key={this.cardViewActive() ? "cards" : "grid"}
                             style={{border: "1px solid #D8DDE4"}}
                             columns={columns}
                             pinnedColumns={pinnedColumns}
                             columnVisibilityModel={this.state.columnVisibilityModel}
                             rows={loadingRows}
                             fullWidth={true}
                             density="compact"
                             filterMode="client"
                             hideFooter={true}
                             hideFooterRowCount={true}
                             hideFooterPagination={true}
                             hideFooterSelectedRowCount={true}
                             loading={false}
                             {...headerless}
                             rowHeight={rowHeight}
                    sx={{
                        '.MuiDataGrid-columnHeaderTitleContainer': {
                            display: "inline",
                        }
                    }}/>
            )
        }
    }

    // " (N)" appended to the table title, where N is the EC's total row count.
    // Omitted while the count is unknown (-1, e.g. before the first load).
    titleRowCountSuffix() {
        const ec = this.applet && this.applet.getEnterpriseComponent ? this.applet.getEnterpriseComponent() : null;
        const total = ec && ec.getTotalRowcount ? ec.getTotalRowcount() : -1;
        return total >= 0 ? ` (${total})` : "";
    }

    getPickableColumns = (columns) => columns.filter((c) => c.headerName !== undefined && c.headerName !== null && c.headerName !== "");

    renderColumnSelector = (columns) => {
        const pickable = this.getPickableColumns(columns);
        return (
            <Menu
                anchorEl={this.state.columnMenuTarget}
                open={Boolean(this.state.columnMenuTarget)}
                onClose={() => this.setState({columnMenuTarget: null})}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{
                    paper: {elevation: 0, sx: {boxShadow: "none", borderRadius: 0, border: "1px solid #A8A9AF", marginTop: "2px"}},
                    list: {dense: true, disablePadding: true, sx: {minWidth: "220px", maxHeight: "60vh", "& .MuiMenuItem-root": {minHeight: "34px"}}},
                }}
            >
                {pickable.map((col) => {
                    const required = col.hideable === false;
                    const visible = this.state.columnVisibilityModel[col.field] !== false;
                    return (
                        <MenuItem key={col.field} disabled={required}
                            onClick={() => { if (!required) this.setColumnVisibility(col.field, !visible); }}>
                            <ListItemIcon sx={{minWidth: "32px"}}>
                                <Checkbox edge="start" size="small" disableRipple tabIndex={-1}
                                    checked={required || visible} disabled={required} sx={{padding: "2px"}}/>
                            </ListItemIcon>
                            <ListItemText primary={col.headerName} secondary={required ? "required" : null}
                                primaryTypographyProps={{fontSize: "14px", color: "#808080"}}
                                secondaryTypographyProps={{fontSize: "11px", color: "#A8A9AF"}}/>
                        </MenuItem>
                    );
                })}
            </Menu>
        );
    };

    render() {
        let loading = this.state.fetchBackgroundOperation === true;
        let columns = this.getColumns(this.props.breakpoint?.getBreakPointName(), false);
        let pinnedColumns = this.getPinnedColumns(this.props.breakpoint?.getBreakPointName());
        let rows = loading ? [] : this.getRows(this.props.breakpoint?.getBreakPointName(), columns);

        return (
            <>
                {this.renderExtension()}
                <div key={1} style={{display: "flex", flexDirection: "column", width: "100%"}}>
                    <div style={{display: "flex", flexDirection: "row", width: "100%", height: "30px", marginLeft: "auto", marginRight: "auto", justifyContent: "space-between"}}>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                            {this.props.title !== null ? (
                                <div style={{fontSize: "14pt", fontWeight: "500", color: "#1498D8", margin: "-4px 0 0 0", overflow: "hidden", display: "flex", flexDirection: "row", alignItems: "center"}}>
                                    {this.props.icon ? (
                                        <span style={{display: "flex", alignItems: "center", color: "#1498D8", marginRight: 6}}>{this.props.icon}</span>
                                    ) : null}
                                    <Typography style={{fontSize: 22, fontWeight: 500, color: "#1498D8"}}>{this.props.title}<span style={{fontWeight: 400, color: Grey5}}>{this.titleRowCountSuffix()}</span></Typography>
                                </div>
                            ): null}
                            {this.props.menu !== null ? (
                                <div style={{fontSize: "14pt", fontWeight: "500", color: "#1498D8", margin: "-3px 0 0 0", overflow: "hidden"}}>
                                    {this.renderMenu()}
                                </div>
                            ): null}
                        </div>
                        <div>
                            {loading ? (
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                    <div style={{margin: "-3px 0 0 0"}}>
                                        <img width="35" height="35" src={spinner} alt="spinner" />
                                    </div>
                                    <div className={css_cres_table.BlinkingText} style={{margin: "4px 0 0 5px"}}>
                                         <Typography style={{fontSize: "14px", fontWeight: 400, color: Grey4}}>Loading...</Typography>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center"}}>
                            {/* A SLOT FOR THE TABLE'S OWN ACTIONS, before the chrome that every
                                table has. Columns, sort and export are things you do to the VIEW;
                                whatever goes here is something you do to the RECORDS, so it reads
                                first and is separated from them by the divider below.

                                A PROP RATHER THAN A SUBCLASS HOOK, because the node needs the
                                page's state -- which rows are ticked -- and a page already holds
                                that. Rendered only when given, so no existing table changes. */}
                            {this.props.actions ? (
                                <div style={{marginTop: "-9px", display: "flex", alignItems: "center", gap: 10}}>
                                    {this.props.actions}
                                </div>
                            ) : null}
                            {/* THE VIEW TOGGLE, and only where a table declared a card. A grid
                                answers "which one" and a card answers "what is this"; neither
                                replaces the other, so this switches the painting rather than the
                                table. Absent on every table that has not declared a card, so no
                                existing grid grows a control that would do nothing. */}
                            {this.props.card ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={this.cardViewActive() ? "Show as a table"
                                                                          : "Show as cards"}>
                                        <IconButton disableRipple={true}
                                                    onClick={this.toggleCardView}>
                                            {this.cardViewActive() ? <TableRowsOutlined/>
                                                                   : <ViewAgendaOutlined/>}
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            ) : null}
                            {this.props.enableColumnSelection !== false && !this.cardViewActive()
                             && this.getPickableColumns(columns).some((c) => c.hideable !== false) ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={"Columns"}>
                                        <IconButton disableRipple={true} onClick={(event) => this.setState({columnMenuTarget: event.currentTarget})}>
                                            <ViewColumnOutlined />
                                        </IconButton>
                                    </Tooltip>
                                    {this.renderColumnSelector(columns)}
                                </div>
                            ): null}
                            {this.props.sort && (this.props.enableSort === undefined || this.props.enableSort === true) ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={"Sort"}>
                                        <IconButton disableRipple={true} onClick={(event) => {
                                            this.setState({sortMenuTarget: event.currentTarget});
                                            //this.setState({showSortSelection: true})
                                        }}>
                                            <Sort />
                                        </IconButton>
                                    </Tooltip>
                                    {this.renderSortSelector()}
                                </div>
                            ): null}
                            {(this.props.enableSearch === undefined || this.props.enableSearch === true) && this.props.simpleSearch ? (
                                <div style={{marginTop: "-9px"}}>
                                    {this.state.showSearch ? (
                                        <Tooltip title={"Search"}>
                                            <IconButton disableRipple={true}
                                                onClick={() => {
                                                    // Closing search clears any active filter and runs a
                                                    // fresh, unfiltered query against the server (page 1 of
                                                    // the full list). Null the searchSpecification first so
                                                    // onSearchResetHandler doesn't re-apply the prior filter.
                                                    this.setSearchSpecification(null);
                                                    this.memoryState.pageToDisplay = 1;
                                                    this.onSearchResetHandler();
                                                    this.setState({showSearch: false});
                                                }}>
                                                <Close sx={{color: "#FF0000"}}/>
                                            </IconButton>
                                        </Tooltip>
                                    ): (
                                        <Tooltip title={"Search"}>
                                            <IconButton disableRipple={true}
                                                onClick={() => {
                                                    if (this.state.showSearch) {
                                                        EventBus.components.emit(this.applet.getEnterpriseComponent().serialNumber + "-form-applet-reset");
                                                        EventBus.components.emit(this.applet.getEnterpriseComponent().serialNumber + "-form-applet-cancel");
                                                        this.applet.getEnterpriseComponent().executeQueryRollback()
                                                        this.setState({showSearch: false});
                                                    } else {
                                                        this.setState({showSearch: true});
                                                    }
                                                }}>
                                                <Search />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {this.state.showSearch && this.props.simpleSearch ? (
                        this.renderSearch()
                    ) : null}
                    <div style={{width: "100%"}}>
                        {loading ? this.renderLoadingTable() : (
                            /* REMOUNTED WHEN THE VIEW CHANGES, and this key is load-bearing. MuiDataGrid CACHES
                             measured row heights: once rows have been measured under
                             getRowHeight: () => "auto" for the cards, switching the SAME instance
                             back to a fixed rowHeight leaves those already-measured rows at their
                             auto heights -- and an auto height for a one-line text row is SHORTER
                             than 48px. The symptom is exactly what Ed saw: "narrow at the top,
                             correct after about 10 rows", the ten being what had been measured.
                             A different key discards the cache with the instance. */
                             <DataGridPro key={this.cardViewActive() ? "cards" : "grid"}
                                         style={{border: "1px solid #D8DDE4", borderRadius: this.state.showSearch ? "0 0 5px 5px" : "5px 5px 5px 5px"}}
                                         columns={columns}
                                         pinnedColumns={pinnedColumns}
                                         columnVisibilityModel={this.state.columnVisibilityModel}
                                         onColumnVisibilityModelChange={this.onColumnVisibilityModelChange}
                                         rows={rows}
                                         fullWidth={true}
                                         density="compact"
                                         filterMode="client"
                                         hideFooter={true}
                                         hideFooterRowCount={true}
                                         hideFooterPagination={true}
                                         hideFooterSelectedRowCount={true}
                                         onRowDoubleClick={(params) => this.onRowDoubleClick(params)}
                                         onRowClick={(params) => this.onRowClick(params)}
                                         getRowClassName={(params) => this.rowClassName(params)}
                                         loading={loading}
                                         {...(this.cardViewActive()
                                             // NO COLUMN HEADER IN CARD VIEW. There is exactly
                                             // one column and it is the record, so the header is a
                                             // bar labelling nothing -- and with an empty
                                             // headerName it drew as a blank strip above the first
                                             // card, which reads as a rendering fault rather than
                                             // as a header. Asked for by Ed.
                                             //
                                             // columnHeaderHeight AND the sx rule, because the
                                             // height alone leaves the container's bottom border:
                                             // a 1px line across the top of the list with nothing
                                             // above it, which is the same complaint one pixel
                                             // smaller.
                                             ? {getRowHeight: () => "auto", columnHeaderHeight: 0}
                                             : (this.props.rowHeight
                                                 ? {rowHeight: this.props.rowHeight} : {}))}
                                sx={{
                                    '& .MuiDataGrid-columnHeaders': {
                                      backgroundColor: '#FEFEFE',     // Solid color
                                      // Or with opacity / gradient
                                      // backgroundColor: 'rgba(25, 118, 210, 0.8)',
                                    },
                                    '.MuiDataGrid-columnHeaderTitleContainer': {
                                        display: "inline",
                                    },
                                    '& .MuiDataGrid-cell': {
                                      backgroundColor: '#ffffff', // All cells
                                    },
                                    '& .MuiDataGrid-row': {
                                      borderBottomColor: '#FEFEFE',
                                      borderBottomWidth: '1px',
                                      borderBottomStyle: 'solid',
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell': {
                                      backgroundColor: '#ffffff', // Striped effect
                                    },
                                    // CARD VIEW HAS NO HEADER AT ALL. columnHeaderHeight collapses
                                    // it; this removes the element, and with it the bottom border
                                    // that would otherwise rule a line across the top of the list.
                                    //
                                    // LAST, AND THAT POSITION IS LOAD-BEARING. An object literal
                                    // takes the LAST key of a repeated name, so this spread placed
                                    // ABOVE the '& .MuiDataGrid-columnHeaders' rule a few lines up
                                    // would be silently overwritten by it -- written, shipped, and
                                    // doing nothing, with no error anywhere.
                                    ...(this.cardViewActive()
                                        ? {'& .MuiDataGrid-columnHeaders': {display: 'none'},
                                           '& .MuiDataGrid-columnHeader': {display: 'none'}}
                                        : {}),
                                }}/>
                            )}
                    </div>
                    <div style={{display: "flex", flexDirection: "row", width: "100%", height: "30px", paddingTop: "3px", marginLeft: "auto", marginRight: "auto", justifyContent: "flex-start", background: "#FFFFFF",}}>
                        {this.applet.getEnterpriseComponent().getMode() !== EnterpriseComponent.MODE_QUERY ? this.renderPagination() : null}
                    </div>
                </div>
            </>
        );
    }

    renderEmptyHeader = (___, params, __, _) => {
        return null;
    }

    renderExtension() {
        return null;
    };

    renderAccountUserNameCell = (___, params, __, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;

        // NULL *AND* UNDEFINED, and the row itself. The guard tested `=== null` only, and a Map
        // returns UNDEFINED for a key it does not hold -- so an absent name walked past it and
        // .split threw, taking the whole table down with an uncaught runtime error rather than
        // rendering one empty cell. Absent and empty are not the same thing, and only one of them
        // was being handled. Reported in t6; this file is identical in six frontends.
        const row = this.state.buffer[params.row.id];
        const value = row ? row.get(params.field) : undefined;
        if (value === null || value === undefined || value === "") return "--";

        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", height: "100%"}}>
                <div key={1} style={{margin: "auto 0 auto 0"}}>
                    <Avatar sx={{bgcolor: Blue3, width: 25, height: 25, fontSize: 14, fontWeight: 500}}>{String(value).split(" ").filter(Boolean).map(word => word[0]).join("").substring(0, 2)}</Avatar>
                </div>
                <div key={2} style={{margin: "auto 0 auto 7px"}}>
                    <Typography style={{fontSize: "14px", fontWeight: 400, color: "#000000"}}>{value}</Typography>
                </div>
            </div>
        );
    };

    renderLocalDateCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        let content

        if (buffer[params.row.id].get(params.field) === null) content = "--";
        else content = formatLocalDate(buffer[params.row.id].get(params.field))

        return <div style={{whiteSpace: "nowrap", width: "100%", display: "block", color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis", alignContent: "center"}}><Typography style={{fontSize: "14px"}}>{content}</Typography></div>
    };

    renderLocalDatetimeCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        let content

        if (buffer[params.row.id].get(params.field) === null) content = "--";
        else content = formatLocal(buffer[params.row.id].get(params.field))

        return <div style={{whiteSpace: "nowrap", width: "100%", display: "block", color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis", alignContent: "center"}}><Typography style={{fontSize: "14px"}}>{content}</Typography></div>
    };

    renderMenuContent() {
        let menuItems = [];

        let elementConfig = {
            options: [],
        };
        for (let i = 0; i < this.props.menu.options.length; i++) {
            let requiredPrivileges = this.props.menu.options[i].privileges;
            let option = {
                displayValue: this.props.menu.options[i].displayValue,
                value: this.props.menu.options[i].value,
                active: this.shouldRenderMenuItem(this.props.menu.options[i].value)
            };
            if (requiredPrivileges === undefined) {
                elementConfig.options.push(option);
            } else {
                let assignedPrivileges = this.props.appContext.sessionContext.getPrivileges();
                let permissionFound = false;
                for (let i = 0; i < requiredPrivileges.length; i++) {
                    for (let j = 0; j < assignedPrivileges.length; j++) {
                        if (requiredPrivileges[i] === assignedPrivileges[j]) {
                            elementConfig.options.push(option);
                            permissionFound = true;
                        }
                    }
                }
                if (!permissionFound) {
                    option.active = false;
                    elementConfig.options.push(option);
                }
            }
        }

        for (let i = 0; i < elementConfig.options.length; i++) {
            menuItems.push(
                <MenuItem disabled={!elementConfig.options[i].active} key={"menuItem" + i} onClick={(e) => {
                    e.stopPropagation();
                    this.handleMenuClick(elementConfig.options[i].value);
                    this.setState({showMenu: false});
                }}>
                    {elementConfig.options[i].displayValue}
                </MenuItem>,
            );
        }

        return menuItems;
    }

    renderMenu() {
        let menu = null;
        let content = null;

        if (this.props.menu && this.props.menu.options.length !== 0) {
            let menuItems = this.renderMenuContent();

            menu = (
                <Menu key={"menu"} id="tableMenu" anchorEl={this.state.menuTarget} open={true} onClose={() => this.setState({menuTarget: null, showMenu: false})} elevation={2}>
                    {menuItems}
                </Menu>
            );
            if (this.state.showMenu === false) menu = null;

            content = (
                <>
                    <Button className={css_cres_table.MenuButton} style={{marginLeft: "-5px", marginTop: "0px"}} variant="text"
                            onClick={(event) => {
                                event.stopPropagation();
                                this.setState({menuTarget: event.currentTarget, showMenu: true});
                            }}
                            endIcon={<MoreVert sx={{width: "25px", height: "25px"}} />}>
                    </Button>
                    {menu}
                </>
            );
        }
        return content;
    };

    renderPagination = () => {
        let pageSizeSelector = (
            <div style={{display: "flex", alignItems: "center", marginLeft: "20px", marginTop: "-5px"}}>
                <Typography component="div" style={{fontSize: "13px", color: "#7b8291"}}>Results per page:</Typography>
                <TextField
                    select={true}
                    key="1"
                    margin="dense"
                    id="PageSizeSelector"
                    onChange={(event) => this.onPageSizeHandler(event)}
                    value={this.memoryState.pageSize}
                    variant={"standard"}
                    slotProps={{
                        input: {},

                        select: {
                            MenuProps: {
                                PaperProps: {
                                    elevation: 1,
                                },
                            },
                            className: css_cres_table.SelectPage,
                            IconComponent: ExpandMore,
                        },

                        inputLabel: {shrink: true}
                    }}>
                    <MenuItem key={1} value={5}>{5}</MenuItem>
                    <MenuItem key={2} value={10}>{10}</MenuItem>
                    <MenuItem key={3} value={20}>{20}</MenuItem>
                    <MenuItem key={4} value={40}>{40}</MenuItem>
                </TextField>
            </div>
        );

        let pagination = null;
        if (this.applet.getEnterpriseComponent().getTotalRowcount() > 0) {
            let recordDisplayString;
            if (this.state.buffer.length === 0) {
                recordDisplayString = "0 of 0";
            } else {
                let firstRecordOnDisplay = (this.memoryState.pageToDisplay - 1) * this.memoryState.pageSize + 1;
                let lastRecordOnDisplay = firstRecordOnDisplay + this.memoryState.pageSize - 1;
                if (lastRecordOnDisplay >= this.applet.getEnterpriseComponent().getTotalRowcount()) {
                    lastRecordOnDisplay = this.applet.getEnterpriseComponent().getTotalRowcount();
                }
                if (this.applet.isRowcountKnown() === true) {
                    if (lastRecordOnDisplay > this.applet.getEnterpriseComponent().getTotalRowcount()) {
                        lastRecordOnDisplay = this.applet.getEnterpriseComponent().getTotalRowcount();
                    }
                    recordDisplayString = firstRecordOnDisplay + " - " + lastRecordOnDisplay + " of " + this.applet.getEnterpriseComponent().getTotalRowcount();
                } else {
                    if (lastRecordOnDisplay > this.state.buffer.length) {
                        lastRecordOnDisplay = this.state.buffer.length;
                    }
                    recordDisplayString = firstRecordOnDisplay + " - " + lastRecordOnDisplay + " of " + this.state.buffer.length;
                }
            }

            let recordDisplay = (
                <div style={{marginTop: "2px"}}>
                    <div>
                        <span>{recordDisplayString}</span>
                    </div>
                </div>
            );

            pagination = (
                <React.Fragment>
                    {pageSizeSelector}
                    <div style={{width: "20px"}} />
                    <Pagination color="primary"
                                count={this.memoryState.numberOfPages}
                                defaultPage={1}
                                page={this.memoryState.pageToDisplay}
                                size="small"
                                boundaryCount={2}
                                showFirstButton
                                showLastButton
                                shape="rounded"
                                onChange={(object, page) => this.handlePageChange(object, page)} />
                    <div style={{width: "20px"}} />
                    <div style={{marginTop: "2px"}}>
                        <Typography component="div" style={{fontSize: "13px", color: "#7b8291"}}>{recordDisplay}</Typography>
                    </div>
                </React.Fragment>
            );
        }
        return pagination;
    };

    shouldRenderMenuItem(identifier) {
        return true;
    };

    renderRowSelectionIndicatorCell = (___, params, __, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;

        if (this.state.buffer.length === 0 || params.row.id === this.state.buffer.length || this.state.buffer[params.row.id].get("RowSelected") !== true) return;
        return (
            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", height: "100%"}}>
                <ArrowRight/>
            </div>
        );
    };

    renderSearch() {
        MemoryContext.components["SimpleSearchFormApplet-PageSize"] = this.memoryState.pageSize;
        return (
            <div style={{border: "1px solid #D8DDE4", borderWidth: "1px 1px 0px 1px", borderRadius: "5px 5px 0 0", height: "auto"}}>
                <div style={{display: "flex", flexDirection: "column", padding: "20px 10px 0px 10px"}}>
                    <SimpleSearchFormApplet enterpriseComponent={this.searchEnterpriseComponent} controls={this.props.simpleSearch.Controls} actions={this.props.simpleSearch.Actions} defaultAction={"Query"}/>
                </div>
            </div>
        );
    };

    renderSortSelector = () => {
        let menu = null;
        let menuItems = [];

        for (let i = 0; i < this.getSortSelections().length; i++) {
            let sortSelection = this.getSortSelections()[i];
            if (sortSelection.Identifier === undefined) continue;
            if (sortSelection.Options === undefined) continue;
            // VISIBLE IS HONOURED HERE, and was honoured nowhere. Every selection carries the flag
            // and this loop rendered all of them regardless, so the one entry that sets it false --
            // the framework's own "Creation Datetime" fallback, which orders by a RowIdentity most
            // IDOCs do not expose -- appeared in every grid's menu beside the grid's own sorts.
            //
            // Hidden means hidden from the MENU, not disabled: an invisible selection is still the
            // default ordering for a grid that declares none of its own, which is the case the
            // fallback exists for.
            if (sortSelection.Visible === false) continue;

            let sortSelectionOptions = sortSelection.Options;

            let sortContent = [];
            for (let j = 0; j < sortSelectionOptions.length; j++) {
                let sortSelectionOption = sortSelectionOptions[j];
                sortContent.push(sortSelectionOption.DisplayName);
            }
            let sortContentString = sortContent.join(" | ");

            menuItems.push(
                <MenuItem key={"sortMenuItem" + i} onClick={() => {
                    this.handleSortMenuClick(sortSelection);
                }}>
                    <ListItemIcon>
                        {this.state.activeSort.Identifier === sortSelection.Identifier ? <Check /> : null}
                    </ListItemIcon>
                    <ListItemText primary={sortContentString} />
                </MenuItem>,
            );
        }

        menu = (
            <Menu id="sortMenu" anchorEl={this.state.sortMenuTarget} open={true} onClose={() => this.setState({sortMenuTarget: null})} elevation={2}>
                {menuItems}
            </Menu>
        );
        if (this.state.sortMenuTarget === null) {
            menu = null;
        }
        return menu;
    };

    renderStandardSingleCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        return (
            <div>
                    <div className={css_cres_table.UnderlineOnHover} style={{whiteSpace: "nowrap", width: "100%", display: "block", fontSize: 14, color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis"}}>{buffer[params.row.id].get(params.field) ?? "--"}</div>
            </div>
        );
    };

    renderPathCell = (___, params, __, _) => {
        return (
            <div>
                &nbsp;
            </div>
        );
    };

    renderStandardSingleHeader = (___, params, __, _) => {
        let content;

        let sortContent;
        let align;

        if (params.colDef.align === "left") align = "flex-start";
        else if (params.colDef.align === "right") align = "flex-end";
        else if (params.colDef.align === "center") align = "center";

        content = (
            <div style={{width: "100%", paddingTop: "10px", height: "37px", fontSize: 14, color: Black}}>
                <div style={{display: "flex", flexDirection: "row", alignContent: align}}><b>{params.colDef.headerName}</b></div>
            </div>
        )
        if (this.state.activeSort) {
            let sortSelectionOptions = this.state.activeSort.Options;
            for (let j = 0; j < sortSelectionOptions.length; j++) {
                let sortSelectionOption = sortSelectionOptions[j];
                if (sortSelectionOption.SortAttribute === params.colDef.field) {
                    let sortDirection = sortSelectionOption.Direction;

                    sortContent = (
                        <>
                            <Tooltip title={sortSelectionOption.Direction === "DESC" ? "ASC" : "DESC"}>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                    <IconButton className={css_button.sortIconButton} onClick={() => this.handleSortDirectionChange(params.colDef.field)}>
                                        {sortDirection === "DESC" ? <North sx={{width: 15, height: 15}}/> : <South sx={{width: 15, height: 15}}/>}
                                    </IconButton>
                                    <Typography style={{position: "relative", fontSize: "9px", top: "-3px"}}>{j + 1}</Typography>
                                </div>
                            </Tooltip>
                        </>
                    )

                    content = (
                        <div style={{width: "100%", paddingTop: "8px", height: "37px", fontSize: 14, color: Grey11}}>
                            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                                <div style={{display: "flex", flexDirection: "row", alignContent: align, marginTop: "2px"}}><b>{params.colDef.headerName}</b></div>
                                <div style={{display: "flex", flexDirection: "row", alignContent: "flex-end", marginTop: "3px"}}>
                                    {sortContent}
                                </div>
                            </div>
                        </div>
                    )
                }
            }
        }

        return (
            content
        );
    };

    renderTimingCell = (___, params, __, _) => {
        return (
            <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start"}}>
                <div><span style={{color: "#EEEEEE"}}>Created Datetime:</span></div>
                <div>{this.state.buffer[params.row.id]["CreatedDatetime"]}</div>
                <div><span style={{color: "#EEEEEE"}}>End Datetime:</span></div>
                <div>{this.state.buffer[params.row.id]["EndDatetime"]}</div>
            </div>
        );
    };

    setSearchSpecification = (searchSpecification) => {
        this.searchSpecification = searchSpecification;
    };

    setSortSelections = (sortSelections) => {
        this.sortSelections = sortSelections;
    };

    setSortSpecification = (sortSpecification) => {
        this.sortSpecification = sortSpecification;
    };

}

export default withBreakpoint(withAppContext(PlatformTable));